export type FileReject = { file: File; reason: 'size' | 'type' }

/**
 * Partition `files` into accepted and rejected lists.
 *
 * Accept tokens (case-insensitive):
 *   - `.ext`             — extension match
 *   - `type/*`           — MIME major type wildcard
 *   - `type/subtype`     — exact MIME match
 *
 * Order: type is checked first; if wrong type, reason = 'type'.
 * If correct type but too large, reason = 'size'.
 * When `multiple` is false, only the first accepted file is kept.
 */
export function partitionFiles(
  files: File[],
  accept: string,
  maxBytes: number,
  multiple = true,
): { accepted: File[]; rejected: FileReject[] } {
  const tokens = accept
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)

  function typeAccepted(file: File): boolean {
    if (tokens.length === 0) return true
    const mime = file.type.toLowerCase()
    const ext = `.${file.name.split('.').pop() ?? ''}`.toLowerCase()
    return tokens.some((token) => {
      if (token.startsWith('.')) return ext === token
      if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1))
      return mime === token
    })
  }

  const accepted: File[] = []
  const rejected: FileReject[] = []

  for (const file of files) {
    if (!typeAccepted(file)) {
      rejected.push({ file, reason: 'type' })
    } else if (file.size > maxBytes) {
      rejected.push({ file, reason: 'size' })
    } else {
      accepted.push(file)
    }
  }

  if (!multiple && accepted.length > 1) {
    return { accepted: accepted.slice(0, 1), rejected }
  }

  return { accepted, rejected }
}
